import React, { useEffect, useRef, useState } from 'react';
import loadable from '@loadable/component';

import BlueArrow from './images/arrow-blue.svg';
import LeftArrow from './images/left-arrow.svg';
import RightArrow from './images/right-arrow.svg';

import css from './SectionNextBookings.module.css';
import { NamedLink } from '../../../../components';
import { AvatarSquareLarge } from '../../../../components/AvatarSquare/AvatarSquare';
import { DATE_TYPE_DATE, DATE_TYPE_DATETIME } from '../../../../util/types';
import { FormattedDate } from 'react-intl';

const BookingPeriod = props => {
  const { startDate, endDate, dateType, timeZone } = props;
  const timeZoneMaybe = timeZone ? { timeZone } : null;

  const timeFormatOptions =
    dateType === DATE_TYPE_DATE
      ? {
          weekday: 'long',
        }
      : {
          weekday: 'short',
          hour: 'numeric',
          minute: 'numeric',
        };

  const dateFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  return (
    <>
      <div className={css.bookingPeriod}>
        <div className={css.bookingPeriodSectionLeft}>
          <div className={css.dayInfo}>
            <FormattedDate value={startDate} {...timeFormatOptions} {...timeZoneMaybe} />
          </div>
          <div className={css.itemLabel}>
            <FormattedDate value={startDate} {...dateFormatOptions} {...timeZoneMaybe} />
          </div>
        </div>
        {'-'}
        <div className={css.bookingPeriodSectionRight}>
          <div className={css.dayInfo}>
            <FormattedDate value={endDate} {...timeFormatOptions} {...timeZoneMaybe} />
          </div>
          <div className={css.itemLabel}>
            <FormattedDate value={endDate} {...dateFormatOptions} {...timeZoneMaybe} />
          </div>
        </div>
      </div>
    </>
  );
};

const BookingDate = props => {
  const { booking, listing } = props;

  if (!booking) {
    return null;
  }

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;

  // Attributes: displayStart and displayEnd can be used to differentiate shown time range
  // from actual start and end times used for availability reservation. It can help in situations
  // where there are preparation time needed between bookings.
  // Read more: https://www.sharetribe.com/api-reference/marketplace.html#bookings
  const { start, end, displayStart, displayEnd } = booking.attributes;
  const localStartDate = displayStart || start;
  const localEndDateRaw = displayEnd || end;

  const endDay = localEndDateRaw;

  return (
    <div className={css.lineItem}>
      <BookingPeriod
        startDate={localStartDate}
        endDate={endDay}
        dateType={DATE_TYPE_DATETIME}
        timeZone={timeZone}
      />
    </div>
  );
};

const Swiper =
  typeof window !== 'undefined'
    ? require('swiper/react').Swiper
    : loadable(() => import('swiper/react'), {
        ssr: false,
      });
const SwiperSlide =
  typeof window !== 'undefined'
    ? require('swiper/react').SwiperSlide
    : loadable(() => import('swiper/react'), {
        ssr: false,
      });
const Navigation =
  typeof window !== 'undefined'
    ? require('swiper/modules').Navigation
    : loadable(() => import('swiper/modules'), {
        ssr: false,
      });

const PENDING_TRANSITIONS = [
  'transition/request-payment',
  'transition/request-payment-after-inquiry',
];

const isPendingSession = session => {
  const lastTransition = session?.attributes?.lastTransition;
  return PENDING_TRANSITIONS.includes(lastTransition);
};

const SectionNextBookings = props => {
  const {
    config,
    upcomingSessions,
    _queryUpcomingSessionsError,
    _queryUpcomingSessionsInProgress,
    onQueryUpcomingSessions,
  } = props;
  const [currentSlideIndex, setCurrentSlideIndex] = useState(1);
  const _isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const swiperRef = useRef();

  useEffect(() => {
    // Load swiper CSS only in the browser
    if (typeof window !== 'undefined') {
      require('swiper/css/bundle');
      require('swiper/css/navigation');
    }

    if (!upcomingSessions) {
      onQueryUpcomingSessions('sale', config);
    }
  }, []);

  return (
    <div className={css.root}>
      <div className={css.headingContainer}>
        <div className={css.heading}>Upcoming sessions</div>

        {upcomingSessions && (
          <div className={css.slidesCounter}>
            <img
              src={LeftArrow}
              className="hover"
              onClick={() => {
                if (swiperRef) {
                  swiperRef.current.swiper.slidePrev();
                }
              }}
            />
            {`${currentSlideIndex} / ${upcomingSessions?.length}`}
            <img
              src={RightArrow}
              className="hover"
              onClick={() => {
                if (swiperRef) {
                  swiperRef.current.swiper.slideNext();
                }
              }}
            />
          </div>
        )}
      </div>
      <div className={css.sliderContainer}>
        {upcomingSessions ? (
          <Swiper
            className={css.swiper}
            ref={swiperRef}
            modules={[Navigation]}
            navigation
            loop={true}
            slidesPerView={1}
            onSlideChange={swiper => setCurrentSlideIndex(swiper.realIndex + 1)}
          >
            {upcomingSessions?.map((session, index) => {
              const isPending = isPendingSession(session);
              return (
                <SwiperSlide key={index}>
                  <div className={css.slideContainer}>
                    <div className={css.imageContainer}>
                      <AvatarSquareLarge
                        user={session.customer}
                        className={css.avatarDesktop}
                        disableProfileLink
                      />
                    </div>
                    <div className={css.slideContent}>
                      {isPending && (
                        <span className={css.pendingBadge}>Action required</span>
                      )}
                      <div className={css.slideHeading}>
                        <p className={css.name}>
                          {session.customer?.attributes?.profile?.displayName}
                        </p>

                        <div>
                          <p className={css.date}>
                            <BookingDate booking={session.booking} listing={session.listing} />
                          </p>
                        </div>
                      </div>

                      <NamedLink
                        className={css.link}
                        name={'SaleDetailsPage'}
                        params={{ id: session.id.uuid }}
                      >
                        <span>{isPending ? 'Accept or decline' : 'See details'}</span>
                        <img src={BlueArrow} />
                      </NamedLink>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        ) : (
          <p>No upcoming sessions yet. Make that awesome profile of yours and wait for bookings!</p>
        )}
      </div>
    </div>
  );
};

export default SectionNextBookings;
